import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import {
    IconAlertTriangle,
    IconAlertTriangleFilled,
    IconCircleCheck,
    IconCircleCheckFilled,
    IconCircleX,
    IconCircleXFilled,
} from '@tabler/icons-react';

import { Request } from '../../../codegen/graphql';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../stores/auth.store';
import { When } from '../../common';
import {
    BatchQuickActionKind,
    useOptionalBatchExpansion,
} from '../batches/BatchExpansionContext';
import { useOptionalPricingBatchStaging } from '../batches/PricingBatchStagingContext';
import {
    isQuickActionOutcomeAllowed,
    resolvePriceChangeQuickActionTarget,
} from './resolvePriceChangeQuickActionTarget';

type Props = {
    record: Request;
};

const ACTION_ICON_SIZE = 26;

const actionBtnClass = 'h-11 w-11 shrink-0 rounded-lg border';

const disabledActionClasses =
    'cursor-not-allowed border-gray-300 bg-gray-50 text-gray-300 opacity-60 hover:!cursor-not-allowed hover:!border-gray-300 hover:!bg-gray-50 hover:!text-gray-300';

function verbActionBorderClasses(kind: BatchQuickActionKind): string {
    if (kind === 'approve') {
        return 'border-green-500 bg-green-50 text-green-600 hover:!border-green-500 hover:!bg-green-50 hover:!text-green-600';
    }
    if (kind === 'decline') {
        return 'border-red-500 bg-red-50 text-red-600 hover:!border-red-500 hover:!bg-red-50 hover:!text-red-600';
    }
    return 'border-amber-500 bg-amber-50 text-amber-600 hover:!border-amber-500 hover:!bg-amber-50 hover:!text-amber-600';
}

function quickActionBorderClasses(
    activeKind: BatchQuickActionKind | null,
    stagedVerb: BatchQuickActionKind | null,
    kind: BatchQuickActionKind,
): string {
    if (activeKind === kind || stagedVerb === kind) {
        return verbActionBorderClasses(kind);
    }
    return 'border-gray-300 bg-white text-gray-400 hover:!border-gray-300 hover:!bg-white hover:!text-gray-400';
}

export function PricingBatchActionsCell({ record }: Props) {
    const expansion = useOptionalBatchExpansion();
    const batchStaging = useOptionalPricingBatchStaging();
    const { isDivisionManager } = useUser();
    const { userId, standInForUserIds } = useAuthStore();

    const showSuggestInterimQuickAction = !isDivisionManager;

    const allResponsibleUserIds = useMemo(
        () => [userId, ...standInForUserIds].filter(Boolean) as string[],
        [userId, standInForUserIds],
    );

    const quickActionTarget = useMemo(
        () =>
            resolvePriceChangeQuickActionTarget(record, allResponsibleUserIds),
        [record, allResponsibleUserIds],
    );

    const canSuggestInterim =
        showSuggestInterimQuickAction &&
        isQuickActionOutcomeAllowed(
            quickActionTarget,
            'ApprovedWithTemporaryPrice',
        );

    const activeKind: BatchQuickActionKind | null =
        expansion?.getKindFor(record.id) ?? null;

    const stagedVerb = batchStaging?.getQueuedEntry(record.id)?.verb ?? null;

    const toggleQuickAction = useCallback(
        (kind: BatchQuickActionKind) => (event: React.MouseEvent) => {
            event.stopPropagation();
            if (!expansion) {
                return;
            }
            const currentKind = expansion.getKindFor(record.id);

            if (batchStaging) {
                batchStaging.syncStagingWithQuickActionExpansionToggle({
                    requestId: record.id,
                    currentExpansionKind: currentKind,
                    clickedKind: kind,
                });
            }

            expansion.toggle(record.id, kind);
        },
        [batchStaging, expansion, record.id],
    );

    let interimActionLabel = '';
    if (showSuggestInterimQuickAction) {
        interimActionLabel = canSuggestInterim
            ? 'Suggest interim price'
            : 'Interim price already suggested. Waiting for supplier feedback';
    }

    return (
        <Group gap={8} wrap="nowrap" justify="center">
            <Tooltip label="Approve" openDelay={100}>
                <ActionIcon
                    variant="transparent"
                    color="gray"
                    aria-label="Approve price change"
                    className={clsx(
                        actionBtnClass,
                        quickActionBorderClasses(
                            activeKind,
                            stagedVerb,
                            'approve',
                        ),
                    )}
                    onClick={toggleQuickAction('approve')}
                >
                    <When
                        condition={activeKind === 'approve'}
                        fallback={
                            <IconCircleCheck
                                size={ACTION_ICON_SIZE}
                                stroke={1.5}
                            />
                        }
                    >
                        <IconCircleCheckFilled
                            size={ACTION_ICON_SIZE}
                            stroke={1.5}
                        />
                    </When>
                </ActionIcon>
            </Tooltip>
            <Tooltip label="Decline" openDelay={100}>
                <ActionIcon
                    variant="transparent"
                    color="gray"
                    aria-label="Decline price change"
                    className={clsx(
                        actionBtnClass,
                        quickActionBorderClasses(
                            activeKind,
                            stagedVerb,
                            'decline',
                        ),
                    )}
                    onClick={toggleQuickAction('decline')}
                >
                    <When
                        condition={activeKind === 'decline'}
                        fallback={
                            <IconCircleX size={ACTION_ICON_SIZE} stroke={1.5} />
                        }
                    >
                        <IconCircleXFilled
                            size={ACTION_ICON_SIZE}
                            stroke={1.5}
                        />
                    </When>
                </ActionIcon>
            </Tooltip>
            <When condition={showSuggestInterimQuickAction}>
                <Tooltip label={interimActionLabel} openDelay={100}>
                    <span className="inline-flex">
                        <ActionIcon
                            variant="transparent"
                            color="gray"
                            aria-label={interimActionLabel}
                            disabled={!canSuggestInterim}
                            className={clsx(
                                actionBtnClass,
                                canSuggestInterim
                                    ? quickActionBorderClasses(
                                          activeKind,
                                          stagedVerb,
                                          'interim',
                                      )
                                    : disabledActionClasses,
                            )}
                            onClick={toggleQuickAction('interim')}
                        >
                            <When
                                condition={
                                    canSuggestInterim &&
                                    activeKind === 'interim'
                                }
                                fallback={
                                    <IconAlertTriangle
                                        size={ACTION_ICON_SIZE}
                                        stroke={1.5}
                                        className={
                                            canSuggestInterim
                                                ? undefined
                                                : 'text-gray-300'
                                        }
                                    />
                                }
                            >
                                <IconAlertTriangleFilled
                                    size={ACTION_ICON_SIZE}
                                    stroke={1.5}
                                />
                            </When>
                        </ActionIcon>
                    </span>
                </Tooltip>
            </When>
        </Group>
    );
}
